import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import ArcoVue, { Message } from '@arco-design/web-vue'
import '@arco-design/web-vue/dist/arco.css'
import App from './App.vue'
import router from './router'
import { useAuthStore } from './stores/auth'
import { initGlobalWebSocket, connect, disconnect } from '@/composables/useGlobalWebSocket'

// Import global styles
import './assets/styles/main.css'

// Initialize theme
const initializeTheme = () => {
  const savedTheme = localStorage.getItem('theme') || 'light'
  const isDark = savedTheme === 'dark'

  // Apply theme to document
  document.documentElement.setAttribute('data-theme', savedTheme)
  document.body.setAttribute('data-theme', savedTheme)

  // Apply Arco Design theme
  if (isDark) {
    document.body.classList.add('arco-theme-dark')
  } else {
    document.body.classList.remove('arco-theme-dark')
  }
}

// Initialize theme on app start
initializeTheme()

const app = createApp(App)
const pinia = createPinia()

// Initialize auth state before mounting app
app.use(pinia)

// Initialize auth from localStorage (must be after pinia.use)
const authStore = useAuthStore()
authStore.initializeAuth()

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'token' && event.newValue === null) {
      void authStore.redirectToLogin('storage_cleared')
    }
  })
}

// Initialize global WebSocket connection for real-time features
// (mobile bind, notifications, etc.)
console.log('[Main] About to initialize global WebSocket...')
try {
  initGlobalWebSocket()
  console.log('[Main] Global WebSocket initialized')
} catch (e) {
  console.error('[Main] Failed to initialize global WebSocket:', e)
}

// Watch for auth changes to connect/disconnect WebSocket
authStore.$subscribe((mutation, state) => {
  const mutationEvents = mutation.events
    ? Array.isArray(mutation.events)
      ? mutation.events
      : [mutation.events]
    : [];
  const mutationKey = mutationEvents.find((event) => typeof event?.key === 'string')?.key;

  if (state.token && mutationKey === 'token') {
    console.log('[Main] Token added, connecting WebSocket...')
    connect()
  } else if (!state.token && mutationKey === 'token') {
    console.log('[Main] Token removed, disconnecting WebSocket...')
    if (
      window.location.pathname !== '/login' &&
      window.location.pathname !== '/forgot-password' &&
      window.location.pathname !== '/reset-password'
    ) {
      void authStore.redirectToLogin('token_cleared')
    }
    disconnect()
  }
})

app.use(router)
app.use(ArcoVue)
app.use(VueQueryPlugin)

// Add global error handler
app.config.errorHandler = (err, instance, info) => {
  console.error('Vue Error:', err)
  console.error('Error Info:', info)
  console.error('Component Instance:', instance)
}

// Make Message globally available
app.config.globalProperties.$message = Message

// Add development logging
if (((import.meta as unknown as { env: { DEV?: boolean } }).env.DEV)) {
  console.log('Vue App mounting...')
  console.log('Router:', router)
  console.log('Routes:', router.getRoutes())
}

app.mount('#app')

if (((import.meta as unknown as { env: { DEV?: boolean } }).env.DEV)) {
  console.log('Vue App mounted successfully')
}
